import { addTocart } from "./cart";
import { getInvalidDIYMeasurements, getInvalidStandardSizes } from "./formValidation";
import { getDIYProperties, getMadeToMeasureProperties, getSendRefProperties, getStandardSizeProperties, getVariantId, getVirtualConsultationProperties } from "./itemPropsHelper";
import { getMeasureTypeOption } from "./variantsUtil";

function getUpdateMeasurementOptions(sizeSettings, sizeOption = 'NEW') {
  const {
    measurementOptions,
    measurementOptionsV2
  } = sizeSettings;

  return {
    selected: (sizeOption != undefined && sizeOption == 'NEW') ? measurementOptionsV2.selected : measurementOptions().selected,
    options: {
      ...measurementOptions()?.options,
      ...measurementOptionsV2?.options
    }
  }
}

function validateMeasurementOptionSelected(context) {
  const {
    sizeSettings,
    sizeOption: {
      sizeOption
    }
  } = context;

  const updatedMeasurementOptions = getUpdateMeasurementOptions(sizeSettings, sizeOption());
  const optionSelected = updatedMeasurementOptions.selected;
  
  if(optionSelected?.trim()?.length < 1) {
    throw {
      message: "Please select a size to proceed"
    }
  }
}

function validateStandardSizeData(context) {
  const {
    sizeSettings: {
      standardSizeData
    }
  } = context;

  const invalidStandardSizeMeasurements = getInvalidStandardSizes(standardSizeData());

  if(invalidStandardSizeMeasurements.length > 0) {
    throw {
      message: `Please select an ${invalidStandardSizeMeasurements[0]} to proceed`
    }
  }
}

function validateDIYData(context) {
  const {
    sizeSettings: {
      diyData,
      measurementStatus,
      MEAS_VALIDATION_STATUS
    }
  } = context;

  const invalidDIYMeasurements = getInvalidDIYMeasurements(diyData().measurements, measurementStatus(), MEAS_VALIDATION_STATUS);

  if(invalidDIYMeasurements.length > 0) {
    throw {
      message: 'Some measurement are invalid.<br> Please correct before proceeding.'
    }
  }
}

function validateFormData(context) {
  const {
    sizeSettings,
    sizeOption: {
      sizeOption
    }
  } = context;

  const {
    errorMessage,
    handlers: {
      handleErrorMessage
    }
  } = sizeSettings;

  try {
    validateMeasurementOptionSelected(context);

    const updatedMeasurementOptions = getUpdateMeasurementOptions(sizeSettings, sizeOption());
    const measurementOptionSelected = updatedMeasurementOptions.selected;

    switch(measurementOptionSelected) {
      case "standard-size":
        validateStandardSizeData(context);
        break;
      case "measure-it-yourself":
        validateDIYData(context);
        break;
    }

    return true;
  } catch(error) {
    const { message } = error;

    if(errorMessage() != message) {
      handleErrorMessage(message);
    }

    return false;
  }
}

export function validateAccentData(formData, accentSettings) {
  if(accentSettings.options?.length < 1) return null;
  
  const selectedAccentSettings = accentSettings.options.find(option => option.id == formData.selected);
  const selected = formData.selected;
  
  for(const [fieldId, field] of Object.entries(selectedAccentSettings.fields ?? {})) {
    const value = () => formData?.option_inputs?.[selected]?.[fieldId]?.trim();
    if(field.required && value()?.length == 0) return `Accents: ${field.label} is required.`
    
    if(field.validation && !(new RegExp(field.validation).test(value()))) {
      return `Accents: ${field.validationError}` ?? `Accents: ${field.label} value is invalid.`
    }
  }

  return null;
}

function constructProperties(context) {
  const {
    color: {
      color
    },
    fit: {
      fit
    },
    styleCustomizations: {
      styleCustomizations,
    },
    sizeSettings,
    sizeOption: {
      sizeOption
    },
    productInfo,
    fitGroupId,
    baseDimensionsMF,
    PRODUCT_REFERENCE_NOUN
  } = context;

  const {
    diyData,
    sizeData,
    standardSizeData,
    sendRefData,
    measurementsInfo
  } = sizeSettings;

  const measurementsEnabled = Object.values(diyData().measurements)
    .filter(measurement => measurement.isEnabled)
    .map(measurement => measurement.id || handleize(measurement.label))

  const updatedMeasurementOptions = getUpdateMeasurementOptions(sizeSettings, sizeOption());
  const measurementOptionSelected = updatedMeasurementOptions.selected;

  const commonProps = {
    color: color(),
    fit: fit(),
    sendRefData: sendRefData(),
    measurementOptionsInfo: updatedMeasurementOptions,
    styleCustomizations: styleCustomizations(),
    productInfo,
    fitGroupId,
    PRODUCT_REFERENCE_NOUN
  }

  let properties;
  switch(measurementOptionSelected) {
    case "standard-size":
      properties = getStandardSizeProperties({
        ...commonProps,
        sizeData: sizeData(),
        standardSizeData: standardSizeData(),
        gradedSpecMF: baseDimensionsMF,
        measurementsEnabled: measurementsEnabled,
      })
      break;
    case "measure-it-yourself":
      properties = getDIYProperties({
        ...commonProps,
        diyData: diyData(),
        gradedSpecMF: baseDimensionsMF,
        measurementsInfo: measurementsInfo,
        canAddMeasurements: true
      })
      break;
    case "send-us-garment":
    case "we-arrange-pickup":
    case "send-fitting-garment":
      properties = getSendRefProperties(commonProps)
      break;
    case "made-to-measure":
      properties = getMadeToMeasureProperties(commonProps)
      break;
    case "virtual-consultation":
      properties = getVirtualConsultationProperties(commonProps)
      break;
  }

  properties._experiment_key = window.experimentFlags;
  return properties;
}

function getCartItemVariantId(context) {
  const {
    color: {
      color
    },
    sizeSettings,
    sizeOption: {
      sizeOption
    },
    productInfo
  } = context;

  const {
    sendRefData
  } = sizeSettings;

  const updatedMeasurementOptions = getUpdateMeasurementOptions(sizeSettings, sizeOption());

  const customerMetafieldValue = (typeof customerMetafield == "undefined") ? null : customerMetafield;
  const measureTypeOption = getMeasureTypeOption(updatedMeasurementOptions.selected, updatedMeasurementOptions.options, customerMetafieldValue, sendRefData());

  return getVariantId(color().selected, measureTypeOption, productInfo);
}

export const handleAddToBagSubmit = async (context, submitType="addToCart", callback=null, analyticsProps={}) => {

  // Find invalid form data and handle error message
  const isValidForm = validateFormData(context());
  if(!isValidForm) return;

  // build line item properties based on form data and measurement option
  const properties = constructProperties(context());
  const variantId = getCartItemVariantId(context());

  // handle loading indication while adding to the cart
  if(submitType == "buyNow" && typeof(pushInitiateCheckout) != "undefined") {
    pushInitiateCheckout();
  }

  await addTocart(variantId, properties, 1, false, callback, submitType == "buyNow", analyticsProps)

  // Capture analytics events in both google analytics and posthog

  // Add to cart using properties constructed and item data

  // Handle Notify Me

  // Execute Callback functions
  console.log("end of handleAddToBagSubmit, ", variantId, properties);

}